import { LanguageService } from '../shared/services/language.service';
import { SmwService, StationInfo } from './smw.service';

import { IsNilPipe } from '../pipes/is-nil.pipe';
import { DatexPipe } from '../shared/datex.pipe';
import { RhPipe } from '../shared/rh.pipe';
import { RoundPipe } from '../shared/round.pipe';
import { WindrosePipe } from '../shared/windrose.pipe';
import { ColorMappingCondPipe } from './pipes/color-mapping-cond.pipe';
import { ColorMappingPigeonPipe } from './pipes/color-mapping-pigeon.pipe';
import { ColorMappingTTPipe } from './pipes/color-mapping-tt.pipe';
import { GetCleanedWwPipe } from './pipes/get-cleaned-ww.pipe';
import { GetGmaAlarmColorPipe } from './pipes/get-gma-alarm-color.pipe';
import { GmaPrecipitationTypeMappingPipe } from './pipes/gma-precipitation-type-mapping.pipe';
import { IsNewDayPipe } from './pipes/is-new-day.pipe';
import { WebcamImageUrlPipe } from './pipes/webcam-image-url.pipe';

import { WmIconDirective } from '../shared/wm-icon.directive';

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { isNil } from 'src/utils/utils';

export const MODULES = [
  CommonModule,
  MatCardModule,
  MatTooltipModule,
  RouterModule,
  MatPaginatorModule,
  MatIconModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const DIRECTIVES = [WmIconDirective];
export const PIPES = [
  DatexPipe,
  WindrosePipe,
  RoundPipe,
  IsNewDayPipe,
  WebcamImageUrlPipe,
  GetCleanedWwPipe,
  GmaPrecipitationTypeMappingPipe,
  GetGmaAlarmColorPipe,
  ColorMappingPigeonPipe,
  ColorMappingCondPipe,
  ColorMappingTTPipe,
  RhPipe,
  GetCleanedWwPipe,
  IsNilPipe,
];

@Component({
  selector: 'app-smw-table',
  templateUrl: './smw-table.component.html',
  styleUrls: ['./smw.component.scss'],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
})
export class SmwTableComponent {
  private injector = inject(Injector);
  public smwService = inject(SmwService);
  public languageService = inject(LanguageService);

  station = input<any>();
  stationInfo = input<StationInfo>();
  webcamStatus = input<boolean>();
  smwData = input<any>();

  length = signal(100);
  pageSize = 20;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  pageEvent: PageEvent;
  webcamImages = signal([]);
  tableData = signal<any[]>(undefined);
  parameters = computed(() => {
    const parametersFromData = [];
    if (this.webcamStatus()) {
      parametersFromData.push('webcam');
    }
    if (this.stationInfo().gmaAlarm) {
      parametersFromData.push('gmaAlarm');
    }
    for (const dataset of this.smwData()) {
      for (const parameter of Object.keys(dataset)) {
        if (parameter === 'gmaAlarm') {
          continue;
        }
        if (
          parameter !== 'dtg' &&
          parameter !== 'id_station' &&
          parametersFromData.indexOf(parameter) < 0 &&
          !isNil(dataset[parameter])
        ) {
          parametersFromData.push(parameter);
        }
      }
    }
    return [
      ...this.smwService.allParameters.filter(parameter =>
        parametersFromData.includes(parameter)
      ),
    ];
  });

  webcamImagesFetched = false;

  constructor() {
    this.listenToWebcamStatusChanges();
    this.listenForInitializingTable();
  }

  initTableData(): void {
    const smwData = this.smwData();
    this.tableData.set([...smwData.slice(0, this.pageSize)]);
    this.length.set(smwData.length);
  }

  getWebcamImages(): void {
    this.webcamImagesFetched = true;
    this.smwService.getWebcamImages(this.station(), 360).subscribe(
      data => {
        this.webcamImages.set([...data]);
      },
      err => console.error('getLatestWebcamImage', err)
    );
  }

  onChangePage(pageEvent: PageEvent): void {
    this.tableData.set([
      ...this.smwData().slice(
        pageEvent.pageIndex * pageEvent.pageSize,
        pageEvent.pageSize + pageEvent.pageIndex * pageEvent.pageSize
      ),
    ]);
  }

  private listenForInitializingTable() {
    effect(
      () => {
        this.station();
        const smwData = this.smwData();

        untracked(() => {
          if (!isNil(smwData)) {
            this.initTableData();
          }
        });
      },
      { injector: this.injector }
    );
  }

  private listenToWebcamStatusChanges() {
    effect(
      () => {
        const webcamStatus = this.webcamStatus();

        untracked(() => {
          const station = this.station();
          if (
            !isNil(webcamStatus) &&
            !isNil(station) &&
            !this.webcamImagesFetched
          ) {
            this.getWebcamImages();
          }
        });
      },
      { injector: this.injector }
    );
  }
}
